import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Store, 
  Star, 
  Download, 
  TrendingUp, 
  Upload, 
  DollarSign, 
  Check, 
  Crown,
  Sparkles,
  Music,
  Type,
  Zap,
  ThumbsUp,
  Clock,
  TrendingDown
} from 'lucide-react';
import { supabase } from '@/db/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface MarketplaceTemplate {
  id: string;
  creator_id: string;
  name: string;
  description: string;
  category: string;
  thumbnail_url: string;
  config: any;
  price_points: number;
  download_count: number;
  rating_avg: number;
  rating_count: number;
  revenue_total: number;
  status: string;
  profiles?: {
    username: string;
  };
}

const TemplateMarketplace: React.FC = () => {
  const { user, profile, refreshProfile } = useAuth();
  const [templates, setTemplates] = useState<MarketplaceTemplate[]>([]);
  const [myTemplates, setMyTemplates] = useState<MarketplaceTemplate[]>([]);
  const [myRevenue, setMyRevenue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isPurchaseOpen, setIsPurchaseOpen] = useState(false);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<MarketplaceTemplate | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);

  // 评论表单
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    review_text: '',
    tags: [] as string[],
  });

  const [templateReviews, setTemplateReviews] = useState<Record<string, any[]>>({});
  const [hasPurchased, setHasPurchased] = useState<Record<string, boolean>>({});
  const [reviewLikes, setReviewLikes] = useState<Record<string, boolean>>({});
  const [reviewSortBy, setReviewSortBy] = useState<'likes' | 'rating' | 'time'>('likes');

  const availableTags = ['易用', '效果好', '性价比高', '创意十足', '专业', '适合新手', '值得推荐'];

  // 上传表单
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    category: 'opening',
    price_points: 100,
    config: {},
  });

  const fetchTemplates = async () => {
    const { data } = await supabase
      .from('template_marketplace')
      .select('*, profiles(username)')
      .eq('status', 'approved')
      .order('download_count', { ascending: false });
    setTemplates(data || []);
  };

  const fetchMyTemplates = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('template_marketplace')
      .select('*')
      .eq('creator_id', user.id)
      .order('created_at', { ascending: false });
    setMyTemplates(data || []);

    // 计算总收益
    const totalRevenue = (data || []).reduce((sum: number, t: any) => sum + (t.revenue_total || 0), 0);
    setMyRevenue(totalRevenue);
  };

  const fetchTemplateReviews = async (templateId: string) => {
    const { data } = await supabase
      .from('template_reviews')
      .select('*, profiles(username, avatar_url)')
      .eq('template_id', templateId)
      .order('created_at', { ascending: false });
    
    setTemplateReviews(prev => ({ ...prev, [templateId]: data || [] }));

    // 获取当前用户的点赞状态
    if (user) {
      const reviewIds = (data || []).map((r: any) => r.id);
      const { data: likes } = await supabase
        .from('review_likes')
        .select('review_id')
        .eq('user_id', user.id)
        .in('review_id', reviewIds);
      
      const likesMap: Record<string, boolean> = {};
      likes?.forEach((like: any) => {
        likesMap[like.review_id] = true;
      });
      setReviewLikes(likesMap);
    }
  };

  const handleToggleReviewLike = async (reviewId: string) => {
    if (!user) {
      toast.error('请先登录');
      return;
    }

    try {
      const { data, error } = await supabase.rpc('toggle_review_like', { review_id_param: reviewId });
      
      if (error) throw error;

      const result = data as { success: boolean; liked: boolean; likes_count: number };

      if (result.success) {
        setReviewLikes(prev => ({ ...prev, [reviewId]: result.liked }));
        
        // 更新评论列表中的点赞数
        if (selectedTemplate) {
          setTemplateReviews(prev => ({
            ...prev,
            [selectedTemplate.id]: prev[selectedTemplate.id].map((review: any) =>
              review.id === reviewId ? { ...review, likes_count: result.likes_count } : review
            ),
          }));
        }
      }
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  };

  const getSortedReviews = (reviews: any[]) => {
    if (!reviews) return [];
    
    const sorted = [...reviews];
    
    switch (reviewSortBy) {
      case 'likes':
        return sorted.sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
      case 'rating':
        return sorted.sort((a, b) => b.rating - a.rating);
      case 'time':
        return sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      default:
        return sorted;
    }
  };

  const checkPurchaseStatus = async (templateId: string) => {
    if (!user) return;
    const { data } = await supabase
      .from('template_transactions')
      .select('id')
      .eq('template_id', templateId)
      .eq('buyer_id', user.id)
      .single();
    
    setHasPurchased(prev => ({ ...prev, [templateId]: !!data }));
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchTemplates(), fetchMyTemplates()]);
      setLoading(false);
    };
    init();
  }, [user]);

  const handleUploadTemplate = async () => {
    if (!user) {
      toast.error('请先登录');
      return;
    }

    if (!uploadForm.name.trim()) {
      toast.error('请输入模板名称');
      return;
    }

    try {
      const { error } = await supabase.from('template_marketplace').insert({
        creator_id: user.id,
        ...uploadForm,
        thumbnail_url: 'https://via.placeholder.com/300x200',
        preview_url: '',
        status: 'pending',
      });

      if (error) throw error;

      toast.success('模板已提交审核，审核通过后将上架市场');
      setIsUploadOpen(false);
      setUploadForm({ name: '', description: '', category: 'opening', price_points: 100, config: {} });
      fetchMyTemplates();
    } catch (err: any) {
      toast.error(err.message || '上传失败');
    }
  };

  const handlePurchaseTemplate = async () => {
    if (!selectedTemplate || !user) return;

    setPurchasing(true);
    try {
      const { data, error } = await supabase.rpc('purchase_template', { template_id_param: selectedTemplate.id });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; creator_revenue?: number };

      if (!result.success) {
        toast.error(result.error || '购买失败');
      } else {
        toast.success(`购买成功！创作者获得 ${result.creator_revenue} 积分收益`);
        await Promise.all([fetchTemplates(), refreshProfile()]);
        setHasPurchased(prev => ({ ...prev, [selectedTemplate.id]: true }));
        setIsPurchaseOpen(false);
      }
    } catch (err: any) {
      toast.error(err.message || '购买失败');
    } finally {
      setPurchasing(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedTemplate || !user) return;

    if (!reviewForm.review_text.trim()) {
      toast.error('请输入评论内容');
      return;
    }

    setSubmittingReview(true);
    try {
      const { data, error } = await supabase.rpc('submit_template_review', {
        template_id_param: selectedTemplate.id,
        rating_param: reviewForm.rating,
        review_text_param: reviewForm.review_text,
        tags_param: reviewForm.tags,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string };

      if (!result.success) {
        toast.error(result.error || '提交失败');
      } else {
        toast.success('评论提交成功！');
        setIsReviewOpen(false);
        setReviewForm({ rating: 5, review_text: '', tags: [] });
        await Promise.all([fetchTemplates(), fetchTemplateReviews(selectedTemplate.id)]);
      }
    } catch (err: any) {
      toast.error(err.message || '提交失败');
    } finally {
      setSubmittingReview(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'opening': return <Zap className="h-4 w-4" />;
      case 'transition': return <Sparkles className="h-4 w-4" />;
      case 'subtitle': return <Type className="h-4 w-4" />;
      case 'music': return <Music className="h-4 w-4" />;
      default: return null;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'opening': return '开场动画';
      case 'transition': return '转场特效';
      case 'subtitle': return '字幕样式';
      case 'music': return '背景音乐';
      default: return category;
    }
  };

  const filteredTemplates = (category?: string) => {
    if (!category || category === 'all') return templates;
    return templates.filter(t => t.category === category);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-12 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Store className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
            <p className="text-muted-foreground">加载中...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        {/* 头部 */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-2xl mb-4">
            <Store className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-extrabold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            模板市场
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            购买优质模板快速生成专业视频，或上传自己的模板赚取积分收益
          </p>

          <div className="mt-6 flex items-center justify-center gap-4">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-card border border-border/50 rounded-2xl shadow-sm">
              <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
              <span className="text-sm font-medium text-muted-foreground">我的积分:</span>
              <span className="text-2xl font-bold text-foreground">{profile?.points || 0}</span>
            </div>

            <Button size="lg" className="rounded-xl font-bold h-12 px-8" onClick={() => setIsUploadOpen(true)}>
              <Upload className="h-5 w-5 mr-2" />
              上传模板
            </Button>
          </div>
        </div>

        {/* 我的创作收益 */}
        {myTemplates.length > 0 && (
          <Card className="rounded-2xl border-border/50 mb-8 bg-gradient-to-br from-green-500/5 to-green-500/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-500" />
                我的创作收益
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">总收益</p>
                  <p className="text-3xl font-bold text-green-600">{myRevenue} 积分</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">上架模板</p>
                  <p className="text-3xl font-bold text-foreground">{myTemplates.filter(t => t.status === 'approved').length}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">总下载量</p>
                  <p className="text-3xl font-bold text-foreground">
                    {myTemplates.reduce((sum, t) => sum + t.download_count, 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 模板列表 */}
        <Tabs defaultValue="all" className="w-full">
          <div className="flex justify-center mb-8">
            <TabsList className="bg-muted/50 p-1">
              <TabsTrigger value="all">全部</TabsTrigger>
              <TabsTrigger value="opening">开场动画</TabsTrigger>
              <TabsTrigger value="transition">转场特效</TabsTrigger>
              <TabsTrigger value="subtitle">字幕样式</TabsTrigger>
              <TabsTrigger value="music">背景音乐</TabsTrigger>
            </TabsList>
          </div>

          {['all', 'opening', 'transition', 'subtitle', 'music'].map((category) => (
            <TabsContent key={category} value={category}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredTemplates(category).map((template) => (
                  <Card key={template.id} className="rounded-2xl border-border/50 hover:shadow-lg transition-all duration-300">
                    <CardHeader className="pb-4">
                      <div className="aspect-video bg-muted rounded-xl mb-4 flex items-center justify-center text-6xl">
                        {getCategoryIcon(template.category)}
                      </div>
                      <CardTitle className="text-lg font-bold line-clamp-1">{template.name}</CardTitle>
                      <CardDescription className="text-sm line-clamp-2">{template.description}</CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="flex items-center gap-1">
                          {getCategoryIcon(template.category)}
                          {getCategoryLabel(template.category)}
                        </Badge>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Download className="h-3 w-3" />
                          {template.download_count}
                        </div>
                      </div>

                      {/* 评分显示 */}
                      {template.rating_count > 0 && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 rounded-lg">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={cn(
                                  "h-4 w-4",
                                  star <= Math.round(template.rating_avg)
                                    ? "text-amber-500 fill-amber-500"
                                    : "text-muted-foreground"
                                )}
                              />
                            ))}
                          </div>
                          <span className="text-sm font-bold">{template.rating_avg.toFixed(1)}</span>
                          <span className="text-xs text-muted-foreground">({template.rating_count})</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between px-4 py-3 bg-muted/30 rounded-xl">
                        <span className="text-sm font-medium text-muted-foreground">价格</span>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                          <span className="text-lg font-bold text-foreground">{template.price_points}</span>
                        </div>
                      </div>

                      <div className="text-xs text-muted-foreground">
                        创作者: {template.profiles?.username || '匿名'}
                      </div>
                    </CardContent>

                    <CardFooter className="flex gap-2">
                      {hasPurchased[template.id] ? (
                        <Button
                          className="flex-1 rounded-xl font-bold h-11"
                          variant="outline"
                          onClick={() => {
                            setSelectedTemplate(template);
                            fetchTemplateReviews(template.id);
                            setIsReviewOpen(true);
                          }}
                        >
                          <Star className="h-4 w-4 mr-2" />
                          评价
                        </Button>
                      ) : (
                        <Button
                          className="flex-1 rounded-xl font-bold h-11"
                          onClick={() => {
                            setSelectedTemplate(template);
                            checkPurchaseStatus(template.id);
                            setIsPurchaseOpen(true);
                          }}
                        >
                          <DollarSign className="h-4 w-4 mr-2" />
                          购买
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* 上传模板对话框 */}
        <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl">上传模板</DialogTitle>
              <DialogDescription>
                上传你的视频模板，审核通过后即可上架市场赚取积分收益（平台抽成 20%）
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">模板名称 *</label>
                <Input
                  placeholder="给模板起个名字"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                  maxLength={50}
                  className="rounded-xl"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">模板描述</label>
                <Textarea
                  placeholder="描述模板的特点和适用场景..."
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  maxLength={200}
                  rows={3}
                  className="rounded-xl resize-none"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">模板分类 *</label>
                <Select value={uploadForm.category} onValueChange={(val) => setUploadForm({ ...uploadForm, category: val })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="opening">开场动画</SelectItem>
                    <SelectItem value="transition">转场特效</SelectItem>
                    <SelectItem value="subtitle">字幕样式</SelectItem>
                    <SelectItem value="music">背景音乐</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">定价（积分）*</label>
                <Input
                  type="number"
                  placeholder="100"
                  value={uploadForm.price_points}
                  onChange={(e) => setUploadForm({ ...uploadForm, price_points: parseInt(e.target.value) || 0 })}
                  min={10}
                  max={10000}
                  className="rounded-xl"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  你将获得 {Math.floor(uploadForm.price_points * 0.8)} 积分（80%），平台抽成 {Math.floor(uploadForm.price_points * 0.2)} 积分（20%）
                </p>
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setIsUploadOpen(false)} className="flex-1 rounded-xl">
                取消
              </Button>
              <Button onClick={handleUploadTemplate} className="flex-1 rounded-xl font-bold">
                提交审核
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 购买确认对话框 */}
        <Dialog open={isPurchaseOpen} onOpenChange={setIsPurchaseOpen}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl">确认购买</DialogTitle>
              <DialogDescription className="pt-4">
                {selectedTemplate && (
                  <div className="space-y-4">
                    <div className="aspect-video bg-muted rounded-xl flex items-center justify-center text-6xl">
                      {getCategoryIcon(selectedTemplate.category)}
                    </div>
                    <div>
                      <p className="text-lg font-bold text-foreground">{selectedTemplate.name}</p>
                      <p className="text-sm text-muted-foreground mt-1">{selectedTemplate.description}</p>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-lg">
                      <span className="text-muted-foreground">消耗积分:</span>
                      <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                      <span className="font-bold text-foreground">{selectedTemplate.price_points}</span>
                    </div>
                    <div className="text-sm text-muted-foreground text-center">
                      购买后剩余: <span className="font-bold">{(profile?.points || 0) - selectedTemplate.price_points}</span> 积分
                    </div>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setIsPurchaseOpen(false)} className="flex-1 rounded-xl">
                取消
              </Button>
              <Button onClick={handlePurchaseTemplate} disabled={purchasing} className="flex-1 rounded-xl font-bold">
                {purchasing ? '购买中...' : '确认购买'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 评论对话框 */}
        <Dialog open={isReviewOpen} onOpenChange={setIsReviewOpen}>
          <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">模板评价</DialogTitle>
              <DialogDescription className="pt-4">
                {selectedTemplate && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 bg-muted rounded-xl flex items-center justify-center text-4xl flex-shrink-0">
                        {getCategoryIcon(selectedTemplate.category)}
                      </div>
                      <div className="flex-1">
                        <p className="text-lg font-bold text-foreground">{selectedTemplate.name}</p>
                        <p className="text-sm text-muted-foreground mt-1">{selectedTemplate.description}</p>
                        {selectedTemplate.rating_count > 0 && (
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={cn(
                                    "h-4 w-4",
                                    star <= Math.round(selectedTemplate.rating_avg)
                                      ? "text-amber-500 fill-amber-500"
                                      : "text-muted-foreground"
                                  )}
                                />
                              ))}
                            </div>
                            <span className="text-sm font-bold">{selectedTemplate.rating_avg.toFixed(1)}</span>
                            <span className="text-xs text-muted-foreground">({selectedTemplate.rating_count} 条评价)</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 评分选择 */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">你的评分 *</label>
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                            className="transition-transform hover:scale-110"
                          >
                            <Star
                              className={cn(
                                "h-8 w-8 cursor-pointer",
                                star <= reviewForm.rating
                                  ? "text-amber-500 fill-amber-500"
                                  : "text-muted-foreground hover:text-amber-400"
                              )}
                            />
                          </button>
                        ))}
                        <span className="ml-2 text-sm font-medium">{reviewForm.rating} 星</span>
                      </div>
                    </div>

                    {/* 评论内容 */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">使用体验 *</label>
                      <Textarea
                        placeholder="分享你对这个模板的使用体验..."
                        value={reviewForm.review_text}
                        onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                        maxLength={500}
                        rows={4}
                        className="rounded-xl resize-none"
                      />
                      <p className="text-xs text-muted-foreground text-right">
                        {reviewForm.review_text.length} / 500
                      </p>
                    </div>

                    {/* 标签选择 */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">选择标签（可选）</label>
                      <div className="flex flex-wrap gap-2">
                        {availableTags.map((tag) => (
                          <Badge
                            key={tag}
                            variant={reviewForm.tags.includes(tag) ? 'default' : 'outline'}
                            className="cursor-pointer px-3 py-1 text-sm"
                            onClick={() => {
                              if (reviewForm.tags.includes(tag)) {
                                setReviewForm({ ...reviewForm, tags: reviewForm.tags.filter(t => t !== tag) });
                              } else {
                                setReviewForm({ ...reviewForm, tags: [...reviewForm.tags, tag] });
                              }
                            }}
                          >
                            {reviewForm.tags.includes(tag) && <Check className="h-3 w-3 mr-1" />}
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* 已有评论 */}
                    {templateReviews[selectedTemplate.id] && templateReviews[selectedTemplate.id].length > 0 && (
                      <div className="space-y-3 pt-4 border-t">
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-foreground">用户评价</h4>
                          <Select value={reviewSortBy} onValueChange={(val: any) => setReviewSortBy(val)}>
                            <SelectTrigger className="w-[140px] h-9 rounded-xl">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="likes">
                                <div className="flex items-center gap-2">
                                  <ThumbsUp className="h-3 w-3" /> 最多点赞
                                </div>
                              </SelectItem>
                              <SelectItem value="rating">
                                <div className="flex items-center gap-2">
                                  <Star className="h-3 w-3" /> 最高评分
                                </div>
                              </SelectItem>
                              <SelectItem value="time">
                                <div className="flex items-center gap-2">
                                  <Clock className="h-3 w-3" /> 最新发布
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-3 max-h-[300px] overflow-y-auto">
                          {getSortedReviews(templateReviews[selectedTemplate.id]).map((review: any) => (
                            <div key={review.id} className="p-3 bg-muted/30 rounded-xl">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium">{review.profiles?.username || '匿名'}</span>
                                  <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <Star
                                        key={star}
                                        className={cn(
                                          "h-3 w-3",
                                          star <= review.rating
                                            ? "text-amber-500 fill-amber-500"
                                            : "text-muted-foreground"
                                        )}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(review.created_at).toLocaleDateString('zh-CN')}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">{review.review_text}</p>
                              {review.tags && review.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {review.tags.map((tag: string) => (
                                    <Badge key={tag} variant="secondary" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              <div className="flex items-center gap-2 pt-2 border-t border-border/30">
                                <Button
                                  size="sm"
                                  variant={reviewLikes[review.id] ? 'default' : 'outline'}
                                  className="h-7 px-3 rounded-lg"
                                  onClick={() => handleToggleReviewLike(review.id)}
                                >
                                  <ThumbsUp className={cn("h-3 w-3 mr-1", reviewLikes[review.id] && "fill-current")} />
                                  {review.likes_count || 0}
                                </Button>
                                <span className="text-xs text-muted-foreground">
                                  {review.likes_count > 0 && `${review.likes_count} 人觉得有帮助`}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setIsReviewOpen(false)} className="flex-1 rounded-xl">
                取消
              </Button>
              <Button onClick={handleSubmitReview} disabled={submittingReview} className="flex-1 rounded-xl font-bold">
                {submittingReview ? '提交中...' : '提交评价'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TemplateMarketplace;
